import React, { useEffect, useState } from 'react';
import { UserService } from '../../user/UserService';
import { IUser } from '../../../model/user/IUser';
import { Button, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { formatDate } from '../../../utils/Functions';
import { AdminAccessRightsModal } from './AdminAccessRightsModal';
import { AdminFilter } from './AdminFilter';
import { ImMobile } from 'react-icons/im';
import { ErrorToast } from '../../../utils/toasters';
import Flag from '../../../shared/components/Flag';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';

const ADMIN_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Role' },
    { label: 'Display name' },
    { label: 'Country' },
    { label: 'Email' },
    { label: 'Last login' },
    { label: 'Action', className: 'text-end' },
];

export default function AdminList() {
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
        totalPages: 0,
        role: 'role_admin',
    });
    const [selectedUserData, setSelectedUserData] = useState({
        id: 0,
        adminAcl: [''],
        email: '',
    });
    const [updateList, setUpdateList] = useState(false);
    const [showAclModal, setShowAclModal] = useState(false);
    const [admins, setAdmins] = useState<IUser[]>([]);

    useEffect(() => {
        setDataLoading(true);
        UserService.getUsers(pagination)
            .then(data => {
                setAdmins(data.data);
                setPagination(prev => ({ ...prev, totalPages: Math.ceil(data.count / pagination.perPage) }));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [pagination.page, updateList]);

    const handlePages = (updatePage: number) => {
        setPagination({ ...pagination, page: updatePage });
    };

    const openModal = (id: number, selectedUserAcl: string[], email: string) => {
        setSelectedUserData(prevState => ({
            ...prevState,
            id,
            adminAcl: selectedUserAcl,
            email: email,
        }));
        setShowAclModal(true);
    };

    const closeModal = () => {
        setShowAclModal(false);
        setUpdateList(prevState => !prevState);
    };

    function handleClick() {
        setPagination(prev => {
            return { ...prev, page: 1 };
        });
        setUpdateList(prevState => !prevState);
    }

    function handleChange(evt: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = evt.target;
        const newValue: any = value?.replace(/^@/, '');
        setPagination(prev => ({
            ...prev,
            [name]: newValue, // remove @ on first place
        }));
    }

    return (
        <>
            <AdminAccessRightsModal
                key={selectedUserData.id}
                show={showAclModal}
                closeModal={closeModal}
                id={selectedUserData.id}
                aclArr={selectedUserData.adminAcl}
                email={selectedUserData?.email}
            />
            <h3 className="text-dark fw-bold fs-2 mb-4">Admin List</h3>
            <div
                className="card mb-5 p-4"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick();
                }}
            >
                <AdminFilter handleChanges={handleChange} handleClicks={handleClick} />
            </div>

            <TableLayout
                pagination={pagination}
                listLength={admins.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName={'admins'}
                skeletonColumns={ADMIN_COLUMNS}
                skeletonRows={pagination.perPage}
            >
                <Table responsive className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
                    <thead>
                        <tr className="even">
                            {ADMIN_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {admins?.map(e => (
                            <tr key={e.id + e.email}>
                                <td>{e.id}</td>
                                <td>{e.userType ?? '-'}</td>
                                <td>
                                    {e.displayName}
                                    <br />
                                    {e.firstName ?? '-'} {e.lastName}
                                </td>
                                <td>
                                    <Flag code={e?.contact?.country} />
                                </td>
                                <td>{e.email}</td>
                                <td>
                                    {formatDate(e.lastLogin as string) ?? '-'}
                                    <br /> IP:{e.lastLoginIp ?? '-'}
                                    {e.hasMobileLogin && (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="impressions-tooltip">
                                                    {e?.mobileDate && formatDate(e?.mobileDate)}
                                                </Tooltip>
                                            }
                                        >
                                            <ImMobile size={18} className="cursor-pointer ml-1" />
                                        </OverlayTrigger>
                                    )}
                                </td>
                                <td>
                                    <Button
                                        size="sm"
                                        onClick={() => openModal(e?.id ?? 0, e?.adminAcl ?? [], e?.email)}
                                    >
                                        Edit
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {admins.length < 15 && !!admins.length && <div className="h-180px"></div>}
                </Table>
            </TableLayout>
        </>
    );
}
